<script lang="ts">
    import type { Snippet } from "svelte";
    import debug from "debug";
    import { videoStreamElementsStore } from "../../Stores/PeerStore";
    import { blocker } from "../../Utils/screenBlocker";
    import { highlightFullScreen } from "../../Stores/ActionsCamStore";

    const logger = debug("responsive-action-bar");

    interface Props {
        rightDiv?: HTMLDivElement;
        actionBarWidth: number;
        left?: Snippet;
        center?: Snippet;
        right?: Snippet;
    }

    let { rightDiv = $bindable(), actionBarWidth = $bindable(), left, center, right }: Props = $props();

    let centerPlusRightDiv: HTMLDivElement;
    let centerDivWidth: number = $state(0);
    let leftDivWidth: number = $state(0);
    let rightContentWidth: number = $state(0);

    $effect(() => {
        if (centerPlusRightDiv)
            centerPlusRightDiv.style.minWidth = `${
                mode === "wide"
                    ? Math.min(actionBarWidth * 0.5 + centerDivWidth * 0.5, actionBarWidth - leftDivWidth)
                    : actionBarWidth - leftDivWidth
            }px`;
    });

    $effect(() => {
        if (centerPlusRightDiv) centerPlusRightDiv.style.maxWidth = `${actionBarWidth - leftDivWidth}px`;
    });

    // In "wide" mode, all buttons are displayed and there is space between left and center buttons
    // In "shrunk" mode, some buttons are hidden and there is no space between left and center buttons
    // We switch mode based on whether left + center + right content actually fits in actionBarWidth.
    // Both directions are derived from the same ResizeObserver-driven width measurements, so entering
    // and leaving "shrunk" mode can no longer desync during fast resizes: that used to happen because
    // entering "shrunk" was width-based while leaving it was driven by a separate IntersectionObserver,
    // and the two could disagree mid-resize (leaving "mode" stuck on "shrunk" and the center content
    // off-center even once there was enough room again).
    let mode: "wide" | "shrunk" = $state("wide");

    let fitsWide = $derived(
        actionBarWidth != undefined &&
            leftDivWidth != undefined &&
            centerDivWidth != undefined &&
            rightContentWidth != undefined
            ? leftDivWidth + centerDivWidth + rightContentWidth <= actionBarWidth
            : true,
    );

    let rafId: number | undefined;
    $effect(() => {
        // Wait a frame before switching: on fast resizes, the width observers can report a
        // transient value before the flexbox has fully settled. Re-checking on the next frame
        // avoids flapping between modes.
        const currentFitsWide = fitsWide;
        if (rafId !== undefined) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
            const newMode = currentFitsWide ? "wide" : "shrunk";
            if (newMode !== mode) {
                logger(`Switching to ${newMode} mode`);
                mode = newMode;
            }
        });
    });

    // Заменяет все bind:offsetWidth — без position: relative
    function observeWidth(node: HTMLElement, onChange: (w: number) => void) {
        let cb = onChange;
        const ro = new ResizeObserver((entries) => {
            for (const entry of entries) {
                cb(entry.borderBoxSize[0]?.inlineSize ?? node.offsetWidth);
            }
        });
        ro.observe(node);
        return {
            update: (fn: (w: number) => void) => {
                cb = fn;
            },
            destroy: () => ro.disconnect(),
        };
    }
</script>

<div
    class="@container/actions w-full z-301 transition-all pointer-events-none bp-menu {$videoStreamElementsStore.length >
        0 && $highlightFullScreen
        ? 'hidden'
        : ''}"
>
    <div {@attach blocker} class="gap-1 @md/actions:gap-2 @xl/actions:gap-4 p-1 @md/actions:p-2 @xl/actions:p-6">
        <div class="w-full flex justify-between items-top" use:observeWidth={(w) => (actionBarWidth = w)}>
            <!-- Left bar -->
            <div class="flex-1 flex">
                <div class="flex-none" use:observeWidth={(w) => (leftDivWidth = w)}>
                    {@render left?.()}
                </div>
                {#if mode === "wide"}
                    <div class="w-0 flex-1 min-w-0 flex justify-end"></div>
                {/if}
            </div>
            <!-- Center + right bar -->
            <div
                class="flex justify-between items-top"
                class:flex-none={mode === "wide"}
                class:flex-1={mode === "shrunk"}
                bind:this={centerPlusRightDiv}
            >
                <!-- Center bar -->
                <div class="flex justify-end" use:observeWidth={(w) => (centerDivWidth = w)}>
                    {@render center?.()}
                </div>
                <!-- Right bar -->
                <div
                    class="flex flex-row justify-end overflow-hidden"
                    class:flex-none={mode === "wide"}
                    class:flex-1={mode === "shrunk"}
                    bind:this={rightDiv}
                >
                    <div class="flex flew-row flex-none h-full" use:observeWidth={(w) => (rightContentWidth = w)}>
                        {@render right?.()}
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
